import {
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { FILE_SIDE_WIDTH_STEP, type FileSideColumn } from '@/utils/fileHighlights';
import { cn } from '@/utils/cn';

/**
 * Same grab-and-slide control as journal notes. Filer grows when the handle
 * moves right. Markering grows when it moves left, so both columns can take
 * space from the document.
 */
export function FileSideResizeHandle({
  side,
  value,
  min,
  max,
  label,
  onChange,
  onReset,
}: {
  side: FileSideColumn;
  value: number;
  min: number;
  max: number;
  label: string;
  onChange: (width: number) => void;
  onReset: () => void;
}) {
  const dragging = useRef(false);
  const startX = useRef(0);
  const startWidth = useRef(value);
  const [active, setActive] = useState(false);
  const growRight = side === 'files';

  function clamp(width: number): number {
    return Math.min(max, Math.max(min, Math.round(width)));
  }

  function moved(clientX: number): number {
    const delta = clientX - startX.current;
    return growRight ? delta : -delta;
  }

  function onPointerDown(event: ReactPointerEvent<HTMLDivElement>) {
    if (event.button !== 0) return;
    event.preventDefault();
    dragging.current = true;
    startX.current = event.clientX;
    startWidth.current = value;
    setActive(true);
    if (typeof event.currentTarget.setPointerCapture === 'function') {
      event.currentTarget.setPointerCapture(event.pointerId);
    }
  }

  function onPointerMove(event: ReactPointerEvent<HTMLDivElement>) {
    if (!dragging.current) return;
    onChange(clamp(startWidth.current + moved(event.clientX)));
  }

  function endDrag(event: ReactPointerEvent<HTMLDivElement>) {
    if (!dragging.current) return;
    dragging.current = false;
    setActive(false);
    const node = event.currentTarget;
    if (typeof node.hasPointerCapture === 'function' && node.hasPointerCapture(event.pointerId)) {
      node.releasePointerCapture(event.pointerId);
    }
  }

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const grow =
      event.key === 'ArrowUp' ||
      (growRight ? event.key === 'ArrowRight' : event.key === 'ArrowLeft');
    const shrink =
      event.key === 'ArrowDown' ||
      (growRight ? event.key === 'ArrowLeft' : event.key === 'ArrowRight');
    if (!grow && !shrink) return;
    event.preventDefault();
    onChange(clamp(value + (grow ? FILE_SIDE_WIDTH_STEP : -FILE_SIDE_WIDTH_STEP)));
  }

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      aria-valuemin={min}
      aria-valuemax={max}
      aria-valuenow={value}
      tabIndex={0}
      data-testid={side === 'files' ? 'file-files-resize' : 'file-marking-resize'}
      className={cn('fp-resize', side === 'files' ? 'is-files' : 'is-marking', active && 'is-drag')}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onKeyDown={onKeyDown}
      onDoubleClick={(event) => {
        event.preventDefault();
        onReset();
      }}
    />
  );
}
